import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { MarketingHome, MarketingFeatures, MarketingPricing, isReservedSlug } from './Marketing';
import RsvpPage from './RsvpPage';
import { PrivacyPage, TermsPage, SupportPage, SecurityPage } from './LegalPages';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000/api';

// ─── Theme CSS Generator ───────────────────────────────────────────────────
function generateThemeCSS(site) {
  if (!site) return '';
  return `
    :root {
      --primary: ${site.primaryColor || '#5b6cf0'};
      --secondary: ${site.secondaryColor || '#c026d3'};
      --accent: ${site.accentColor || '#10b981'};
      --text: ${site.textColor || '#ffffff'};
      --bg: ${site.bgColor || '#080a0f'};
      --card-bg: ${site.cardBgColor || '#111318'};
      --border: ${site.themeMode === 'light' ? '#e2e8f0' : '#2a2f3a'};
      --muted: ${site.themeMode === 'light' ? '#64748b' : '#8493ad'};
      --font-heading: '${site.fontHeading || 'Outfit'}', sans-serif;
      --font-body: '${site.fontBody || 'DM Sans'}', sans-serif;
    }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    html { scroll-behavior: smooth; }
    body { font-family: var(--font-body); background: var(--bg); color: var(--text); line-height: 1.6; }
    h1, h2, h3, h4, h5, h6 { font-family: var(--font-heading); font-weight: 700; line-height: 1.2; }
    a { color: var(--primary); text-decoration: none; }
    a:hover { opacity: 0.85; }
    .container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }
    .section { padding: 80px 0; }
    .section-alt { background: var(--card-bg); }
    .section-title { font-size: clamp(28px, 4vw, 42px); margin-bottom: 12px; }
    .section-subtitle { font-size: 16px; color: var(--muted); max-width: 600px; margin-bottom: 40px; }
    .btn { display: inline-flex; align-items: center; justify-content: center; padding: 14px 28px; border-radius: 10px; font-weight: 600; font-size: 15px; cursor: pointer; border: none; transition: all 0.2s; font-family: var(--font-body); }
    .btn-primary { background: var(--primary); color: #fff; }
    .btn-primary:hover { opacity: 0.9; transform: translateY(-1px); }
    .btn-secondary { background: transparent; color: var(--text); border: 2px solid var(--border); }
    .btn-secondary:hover { border-color: var(--primary); color: var(--primary); }
    .btn-accent { background: var(--accent); color: #fff; }
    .card { background: var(--card-bg); border: 1px solid var(--border); border-radius: 14px; padding: 24px; transition: transform 0.2s, box-shadow 0.2s; }
    .card:hover { transform: translateY(-2px); box-shadow: 0 8px 30px rgba(0,0,0,0.15); }
    .grid { display: grid; gap: 20px; }
    .grid-2 { grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); }
    .grid-3 { grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); }
    .grid-4 { grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); }
    input, textarea, select { width: 100%; padding: 12px 16px; border-radius: 10px; border: 1px solid var(--border); background: var(--card-bg); color: var(--text); font-family: var(--font-body); font-size: 14px; }
    input:focus, textarea:focus { outline: none; border-color: var(--primary); }
    .badge { display: inline-flex; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; background: color-mix(in srgb, var(--primary) 20%, transparent); color: var(--primary); }
    @media (max-width: 768px) {
      .section { padding: 50px 0; }
      .container { padding: 0 16px; }
      .grid-3, .grid-4 { grid-template-columns: 1fr; }
    }
    ${site.customCss || ''}
  `;
}

// ─── SEO helpers ───────────────────────────────────────────────────────────
// Emits LocalBusiness (falls back to SportsActivityLocation for gyms), a
// HealthClub subtype for broader fitness businesses, and Event entries for
// the first ~20 recurring classes so Google can surface them in rich results.
function buildJsonLd(site, pageUrl) {
  if (!site?.organization) return {};
  const org = site.organization;
  const s = site.siteSettings || {};
  const address = (org.address || org.city || org.postcode) ? {
    '@type': 'PostalAddress',
    streetAddress: org.address || undefined,
    addressLocality: org.city || undefined,
    addressRegion: org.state || undefined,
    postalCode: org.postcode || undefined,
    addressCountry: org.country || 'AU',
  } : undefined;

  const biz = {
    '@context': 'https://schema.org',
    '@type': org.business_type === 'martial_arts' ? ['SportsActivityLocation', 'LocalBusiness']
            : org.business_type === 'fitness' ? ['HealthClub', 'LocalBusiness']
            : 'LocalBusiness',
    name: org.name,
    url: pageUrl,
    telephone: org.phone || undefined,
    email: org.email || undefined,
    description: s.metaDescription || s.siteTagline || undefined,
    image: s.ogImageUrl || s.logoUrl || undefined,
    address,
  };

  const events = (site.classes || []).slice(0, 20).map((c) => ({
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: c.name,
    eventSchedule: {
      '@type': 'Schedule',
      repeatFrequency: 'P1W',
      byDay: c.day_of_week ? `https://schema.org/${c.day_of_week.charAt(0).toUpperCase() + c.day_of_week.slice(1)}` : undefined,
      startTime: c.start_time || undefined,
    },
    location: { '@type': 'Place', name: org.name, address },
    organizer: { '@type': 'Organization', name: org.name, url: pageUrl },
  }));

  return events.length > 0 ? [biz, ...events] : biz;
}

// ─── Site Page ─────────────────────────────────────────────────────────────
function SitePage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trialForm, setTrialForm] = useState({ firstName: '', lastName: '', email: '', phone: '', message: '' });
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [formMsg, setFormMsg] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await axios.get(`${API_URL}/public/site/${slug}`);
        setData(d);
      } catch { setData(null); }
      setLoading(false);
    })();
  }, [slug]);

  if (loading) return <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#080a0f', color: '#fff' }}>Loading...</div>;
  if (!data) return <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#080a0f', color: '#fff', flexDirection: 'column', gap: 12 }}><h1>Site Not Found</h1><p>This organisation hasn't set up their website yet.</p></div>;

  const { org, site, disciplines, plans, classes, instructors, posts, products } = data;
  const s = site;

  const submitTrial = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/public/site/${slug}/trial`, trialForm);
      setFormMsg(p => ({ ...p, trial: 'success' }));
      setTrialForm({ firstName: '', lastName: '', email: '', phone: '', message: '' });
    } catch { setFormMsg(p => ({ ...p, trial: 'error' })); }
  };

  const submitContact = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/public/site/${slug}/contact`, contactForm);
      setFormMsg(p => ({ ...p, contact: 'success' }));
      setContactForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch { setFormMsg(p => ({ ...p, contact: 'error' })); }
  };

  const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
  const DAY_LABELS = { monday:'Mon', tuesday:'Tue', wednesday:'Wed', thursday:'Thu', friday:'Fri', saturday:'Sat', sunday:'Sun' };

  return (
    <>
      <Helmet>
        <title>{s.siteTitle || org.name}</title>
        <meta name="description" content={s.metaDescription || s.siteTagline || `${org.name} — ${org.city || ''}`} />
        {s.metaKeywords && <meta name="keywords" content={s.metaKeywords} />}
        {/* OpenGraph */}
        <meta property="og:title" content={s.siteTitle || org.name} />
        <meta property="og:description" content={s.metaDescription || s.siteTagline || ''} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={org.name} />
        {s.ogImageUrl && <meta property="og:image" content={s.ogImageUrl} />}
        {typeof window !== 'undefined' && <meta property="og:url" content={window.location.href} />}
        {/* Twitter */}
        <meta name="twitter:card" content={s.ogImageUrl ? 'summary_large_image' : 'summary'} />
        <meta name="twitter:title" content={s.siteTitle || org.name} />
        <meta name="twitter:description" content={s.metaDescription || s.siteTagline || ''} />
        {s.ogImageUrl && <meta name="twitter:image" content={s.ogImageUrl} />}
        {/* Favicon + theme */}
        {s.faviconUrl && <link rel="icon" href={s.faviconUrl} />}
        <style>{generateThemeCSS(s)}</style>
        {/* JSON-LD for Google rich results + local business SEO */}
        <script type="application/ld+json">{JSON.stringify(buildJsonLd({ organization: org, siteSettings: s, classes }, typeof window !== 'undefined' ? window.location.origin + window.location.pathname : ''))}</script>
      </Helmet>

      {/* ─── Nav ─────────────────────────────────────────────────────── */}
      <nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, background: `${s.bgColor || '#080a0f'}ee`, backdropFilter: 'blur(12px)', borderBottom: '1px solid var(--border)' }}>
        <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 64 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {s.logoUrl && <img src={s.logoUrl} alt={org.name} style={{ height: 36, borderRadius: 8 }} />}
            <span style={{ fontSize: 18, fontWeight: 700, fontFamily: 'var(--font-heading)' }}>{org.name}</span>
          </div>
          <div style={{ display: 'flex', gap: 24, fontSize: 14, fontWeight: 500 }}>
            {s.aboutEnabled && <a href="#about" style={{ color: 'var(--muted)' }}>About</a>}
            {s.programsEnabled && <a href="#programs" style={{ color: 'var(--muted)' }}>Programs</a>}
            {s.scheduleEnabled && <a href="#schedule" style={{ color: 'var(--muted)' }}>Schedule</a>}
            {s.blogEnabled && posts.length > 0 && <a href="#blog" style={{ color: 'var(--muted)' }}>Blog</a>}
            {s.storeEnabled && <a href="#store" style={{ color: 'var(--muted)' }}>Store</a>}
            <a href="#contact" style={{ color: 'var(--muted)' }}>Contact</a>
            {s.trialEnabled && <a href="#trial" className="btn btn-primary" style={{ padding: '8px 20px', fontSize: 13 }}>{s.heroCtaText || 'Free Trial'}</a>}
          </div>
        </div>
      </nav>

      <main style={{ paddingTop: 64 }}>
        {/* ─── Hero ────────────────────────────────────────────────── */}
        {s.heroEnabled && (
          <section style={{ position: 'relative', minHeight: '80vh', display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
            {s.heroImageUrl && <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${s.heroImageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.3 }} />}
            <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(135deg, ${s.primaryColor}22, ${s.secondaryColor}22)` }} />
            <div className="container" style={{ position: 'relative', zIndex: 1, maxWidth: 700 }}>
              {s.siteTagline && <div className="badge" style={{ marginBottom: 16 }}>{s.siteTagline}</div>}
              <h1 style={{ fontSize: 'clamp(36px, 5vw, 64px)', fontWeight: 800, lineHeight: 1.1, marginBottom: 20 }}>{s.heroHeadline || `Welcome to ${org.name}`}</h1>
              {s.heroSubheadline && <p style={{ fontSize: 18, color: 'var(--muted)', marginBottom: 32, maxWidth: 560 }}>{s.heroSubheadline}</p>}
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {s.heroCtaText && <a href={s.heroCtaUrl || '#trial'} className="btn btn-primary" style={{ fontSize: 16, padding: '16px 32px' }}>{s.heroCtaText}</a>}
                {s.heroCta2Text && <a href={s.heroCta2Url || '#schedule'} className="btn btn-secondary" style={{ fontSize: 16, padding: '16px 32px' }}>{s.heroCta2Text}</a>}
              </div>
            </div>
          </section>
        )}

        {/* ─── About ───────────────────────────────────────────────── */}
        {s.aboutEnabled && (
          <section id="about" className="section section-alt">
            <div className="container" style={{ display: 'flex', gap: 40, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 280 }}>
                <h2 className="section-title">{s.aboutTitle}</h2>
                <div style={{ fontSize: 16, color: 'var(--muted)', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>{s.aboutContent || `Welcome to ${org.name}. We're dedicated to helping you reach your full potential.`}</div>
              </div>
              {s.aboutImageUrl && <div style={{ flex: 1, minWidth: 280 }}><img src={s.aboutImageUrl} alt="About" style={{ width: '100%', borderRadius: 14, maxHeight: 400, objectFit: 'cover' }} /></div>}
            </div>
            {/* Instructors */}
            {instructors.length > 0 && (
              <div className="container" style={{ marginTop: 50 }}>
                <h3 style={{ fontSize: 22, marginBottom: 20 }}>Our Team</h3>
                <div className="grid grid-4">
                  {instructors.map((inst, i) => (
                    <div key={i} className="card" style={{ textAlign: 'center' }}>
                      <div style={{ width: 80, height: 80, borderRadius: '50%', background: `linear-gradient(135deg, var(--primary), var(--secondary))`, margin: '0 auto 12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 700, color: '#fff', overflow: 'hidden' }}>
                        {inst.photo_url ? <img src={inst.photo_url} alt={inst.display_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : inst.display_name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div style={{ fontSize: 16, fontWeight: 600 }}>{inst.display_name}</div>
                      {inst.title && <div style={{ fontSize: 13, color: 'var(--primary)', marginTop: 4 }}>{inst.title}</div>}
                      {inst.bio && <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 8 }}>{inst.bio}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* ─── Programs ────────────────────────────────────────────── */}
        {s.programsEnabled && (
          <section id="programs" className="section">
            <div className="container">
              <h2 className="section-title">{s.programsTitle}</h2>
              {s.programsSubtitle && <p className="section-subtitle">{s.programsSubtitle}</p>}
              <div className="grid grid-3">
                {disciplines.map(d => (
                  <div key={d.id} className="card">
                    <h3 style={{ fontSize: 20, marginBottom: 4 }}>{d.name}</h3>
                    {d.style && <div style={{ fontSize: 13, color: 'var(--primary)', marginBottom: 12 }}>{d.style}</div>}
                    <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>{d.rank_count} rank levels · {d.grading_system}</div>
                    {plans.filter(p => p.discipline_name === d.name).map(p => (
                      <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid var(--border)', fontSize: 14 }}>
                        <span>{p.name}</span>
                        <span style={{ fontWeight: 700, color: 'var(--accent)' }}>${p.base_price}/{p.frequency}</span>
                      </div>
                    ))}
                    <a href="#trial" className="btn btn-primary" style={{ width: '100%', marginTop: 16, padding: '12px 0' }}>Start Free Trial</a>
                  </div>
                ))}
                {/* Fitness plans without discipline */}
                {plans.filter(p => !p.discipline_name).length > 0 && (
                  <div className="card">
                    <h3 style={{ fontSize: 20, marginBottom: 12 }}>Fitness & PT</h3>
                    {plans.filter(p => !p.discipline_name).map(p => (
                      <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid var(--border)', fontSize: 14 }}>
                        <div><div>{p.name}</div>{p.description && <div style={{ fontSize: 12, color: 'var(--muted)' }}>{p.description}</div>}</div>
                        <span style={{ fontWeight: 700, color: 'var(--accent)', whiteSpace: 'nowrap' }}>${p.base_price}/{p.frequency}</span>
                      </div>
                    ))}
                    <a href="#trial" className="btn btn-primary" style={{ width: '100%', marginTop: 16, padding: '12px 0' }}>Get Started</a>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* ─── Schedule ────────────────────────────────────────────── */}
        {s.scheduleEnabled && classes.length > 0 && (
          <section id="schedule" className="section section-alt">
            <div className="container">
              <h2 className="section-title">{s.scheduleTitle}</h2>
              <p className="section-subtitle">Find a class that fits your schedule.</p>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead>
                    <tr>{DAYS.map(d => <th key={d} style={{ padding: '10px 8px', textAlign: 'left', borderBottom: '2px solid var(--border)', color: 'var(--muted)', fontWeight: 600, fontSize: 12, textTransform: 'uppercase' }}>{DAY_LABELS[d]}</th>)}</tr>
                  </thead>
                  <tbody>
                    <tr>
                      {DAYS.map(day => (
                        <td key={day} style={{ padding: 8, verticalAlign: 'top', borderBottom: '1px solid var(--border)', minWidth: 120 }}>
                          {classes.filter(c => c.day_of_week === day).map(c => (
                            <div key={c.id} style={{ background: 'var(--bg)', borderRadius: 8, padding: '8px 10px', marginBottom: 6, borderLeft: `3px solid var(--primary)` }}>
                              <div style={{ fontWeight: 600, fontSize: 13 }}>{c.name}</div>
                              <div style={{ fontSize: 11, color: 'var(--muted)' }}>{c.start_time?.slice(0, 5)} · {c.duration_mins}min</div>
                              {c.instructor_name && <div style={{ fontSize: 11, color: 'var(--muted)' }}>{c.instructor_name}</div>}
                            </div>
                          ))}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        {/* ─── Blog ────────────────────────────────────────────────── */}
        {s.blogEnabled && posts.length > 0 && (
          <section id="blog" className="section">
            <div className="container">
              <h2 className="section-title">{s.blogTitle}</h2>
              <div className="grid grid-3" style={{ marginTop: 20 }}>
                {posts.map(p => (
                  <a key={p.slug} href={`/${slug}/blog/${p.slug}`} className="card" style={{ textDecoration: 'none', color: 'inherit' }}>
                    {p.featured_image && <img src={p.featured_image} alt={p.title} style={{ width: '100%', height: 180, objectFit: 'cover', borderRadius: 10, marginBottom: 12 }} />}
                    <h3 style={{ fontSize: 17, marginBottom: 8 }}>{p.title}</h3>
                    {p.excerpt && <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>{p.excerpt}</p>}
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 10 }}>{p.published_at ? new Date(p.published_at).toLocaleDateString() : ''}</div>
                  </a>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ─── Store ───────────────────────────────────────────────── */}
        {s.storeEnabled && products.length > 0 && (
          <section id="store" className="section section-alt">
            <div className="container">
              <h2 className="section-title">{s.storeTitle}</h2>
              <div className="grid grid-4" style={{ marginTop: 20 }}>
                {products.map(p => (
                  <div key={p.id} className="card">
                    <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>{p.name}</div>
                    {(p.brand || p.colour) && <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>{[p.brand, p.colour].filter(Boolean).join(' · ')}</div>}
                    <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--accent)' }}>${parseFloat(p.sell_price).toFixed(2)}</div>
                    {p.variants && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>{p.variants.length} sizes available</div>}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ─── Trial Signup ────────────────────────────────────────── */}
        {s.trialEnabled && (
          <section id="trial" className="section" style={{ background: `linear-gradient(135deg, ${s.primaryColor}15, ${s.secondaryColor}15)` }}>
            <div className="container" style={{ maxWidth: 600, textAlign: 'center' }}>
              <h2 className="section-title" style={{ marginBottom: 8 }}>{s.trialTitle}</h2>
              <p className="section-subtitle" style={{ margin: '0 auto 30px' }}>{s.trialSubtitle}</p>
              {formMsg.trial === 'success' ? (
                <div className="card" style={{ background: 'var(--accent)', color: '#fff', padding: 30 }}>
                  <h3>Thank you!</h3><p>We'll be in touch shortly to confirm your trial.</p>
                </div>
              ) : (
                <form onSubmit={submitTrial} style={{ display: 'flex', flexDirection: 'column', gap: 12, textAlign: 'left' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <input placeholder="First Name *" required value={trialForm.firstName} onChange={e => setTrialForm(p => ({ ...p, firstName: e.target.value }))} />
                    <input placeholder="Last Name *" required value={trialForm.lastName} onChange={e => setTrialForm(p => ({ ...p, lastName: e.target.value }))} />
                  </div>
                  <input type="email" placeholder="Email *" required value={trialForm.email} onChange={e => setTrialForm(p => ({ ...p, email: e.target.value }))} />
                  <input placeholder="Phone" value={trialForm.phone} onChange={e => setTrialForm(p => ({ ...p, phone: e.target.value }))} />
                  <textarea placeholder="Tell us about your experience level or any questions" rows={3} value={trialForm.message} onChange={e => setTrialForm(p => ({ ...p, message: e.target.value }))} />
                  <button type="submit" className="btn btn-primary" style={{ width: '100%', fontSize: 16, padding: '16px 0' }}>{s.heroCtaText || 'Book Free Trial'}</button>
                </form>
              )}
            </div>
          </section>
        )}

        {/* ─── Contact ─────────────────────────────────────────────── */}
        {s.contactEnabled && (
          <section id="contact" className="section section-alt">
            <div className="container">
              <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 280 }}>
                  <h2 className="section-title">{s.contactTitle}</h2>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 20, fontSize: 15, color: 'var(--muted)' }}>
                    {org.address && <div><strong style={{ color: 'var(--text)' }}>Address</strong><br />{org.address}{org.city ? `, ${org.city}` : ''} {org.state} {org.postcode}</div>}
                    {org.phone && <div><strong style={{ color: 'var(--text)' }}>Phone</strong><br /><a href={`tel:${org.phone}`}>{org.phone}</a></div>}
                    {org.email && <div><strong style={{ color: 'var(--text)' }}>Email</strong><br /><a href={`mailto:${org.email}`}>{org.email}</a></div>}
                  </div>
                  {s.contactMapEmbed && <div style={{ marginTop: 20 }} dangerouslySetInnerHTML={{ __html: s.contactMapEmbed }} />}
                </div>
                <div style={{ flex: 1, minWidth: 280 }}>
                  {formMsg.contact === 'success' ? (
                    <div className="card" style={{ background: 'var(--accent)', color: '#fff', padding: 30, textAlign: 'center' }}>
                      <h3>Message Sent!</h3><p>We'll get back to you shortly.</p>
                    </div>
                  ) : (
                    <form onSubmit={submitContact} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <input placeholder="Your Name *" required value={contactForm.name} onChange={e => setContactForm(p => ({ ...p, name: e.target.value }))} />
                      <input type="email" placeholder="Email *" required value={contactForm.email} onChange={e => setContactForm(p => ({ ...p, email: e.target.value }))} />
                      <input placeholder="Phone" value={contactForm.phone} onChange={e => setContactForm(p => ({ ...p, phone: e.target.value }))} />
                      <input placeholder="Subject" value={contactForm.subject} onChange={e => setContactForm(p => ({ ...p, subject: e.target.value }))} />
                      <textarea placeholder="Your Message *" required rows={5} value={contactForm.message} onChange={e => setContactForm(p => ({ ...p, message: e.target.value }))} />
                      <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>Send Message</button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      {/* ─── Footer ────────────────────────────────────────────────── */}
      <footer style={{ padding: '40px 0', borderTop: '1px solid var(--border)' }}>
        <div className="container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, fontFamily: 'var(--font-heading)' }}>{org.name}</div>
            {s.footerText && <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>{s.footerText}</div>}
            {org.abn && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>ABN: {org.abn}</div>}
          </div>
          <div style={{ display: 'flex', gap: 16, fontSize: 13 }}>
            {s.socialFacebook && <a href={s.socialFacebook} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--muted)' }}>Facebook</a>}
            {s.socialInstagram && <a href={s.socialInstagram} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--muted)' }}>Instagram</a>}
            {s.socialYoutube && <a href={s.socialYoutube} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--muted)' }}>YouTube</a>}
            {s.socialTiktok && <a href={s.socialTiktok} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--muted)' }}>TikTok</a>}
          </div>
        </div>
        <div className="container" style={{ marginTop: 20, textAlign: 'center', fontSize: 12, color: 'var(--muted)' }}>
          &copy; {new Date().getFullYear()} {org.name}. Powered by <a href="https://phoenixsuite.com.au" style={{ color: 'var(--primary)' }}>Phoenix Suite</a>
        </div>
      </footer>
    </>
  );
}

// ─── Blog Post Page ────────────────────────────────────────────────────────
function BlogPost() {
  const { slug, postSlug } = useParams();
  const [post, setPost] = useState(null);
  const [site, setSite] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [siteRes, postRes] = await Promise.all([
          axios.get(`${API_URL}/public/site/${slug}`),
          axios.get(`${API_URL}/public/site/${slug}/blog/${postSlug}`),
        ]);
        setSite(siteRes.data.site);
        setPost(postRes.data);
      } catch {}
    })();
  }, [slug, postSlug]);

  if (!post || !site) return <div style={{ padding: 60, textAlign: 'center', background: '#080a0f', color: '#fff', minHeight: '100vh' }}>Loading...</div>;

  return (
    <>
      <Helmet>
        <title>{post.meta_title || post.title}</title>
        <meta name="description" content={post.meta_description || post.excerpt || ''} />
        {post.featured_image && <meta property="og:image" content={post.featured_image} />}
        <style>{generateThemeCSS(site)}</style>
      </Helmet>
      <div className="container" style={{ maxWidth: 760, paddingTop: 80, paddingBottom: 60 }}>
        <a href={`/${slug}`} style={{ fontSize: 14, color: 'var(--muted)', display: 'block', marginBottom: 20 }}>&larr; Back to site</a>
        {post.featured_image && <img src={post.featured_image} alt={post.title} style={{ width: '100%', borderRadius: 14, marginBottom: 24, maxHeight: 400, objectFit: 'cover' }} />}
        <h1 style={{ fontSize: 'clamp(28px, 4vw, 42px)', marginBottom: 12 }}>{post.title}</h1>
        <div style={{ fontSize: 14, color: 'var(--muted)', marginBottom: 30 }}>
          {post.author_name && <span>By {post.author_name}</span>}
          {post.published_at && <span> · {new Date(post.published_at).toLocaleDateString()}</span>}
        </div>
        <article style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--muted)', whiteSpace: 'pre-wrap' }}>{post.content}</article>
      </div>
    </>
  );
}

// Guard against a per-org site colliding with a reserved marketing path.
// If someone ever creates an org with slug "pricing", SitePage on /pricing
// would intercept. Redirect those to the marketing page instead.
function SitePageOrMarketing() {
  const { slug } = useParams();
  if (isReservedSlug(slug)) {
    if (slug === 'features') return <MarketingFeatures />;
    if (slug === 'pricing') return <MarketingPricing />;
    return <MarketingHome />;
  }
  return <SitePage />;
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Phoenix Suite marketing site — phoenixsuite.com.au root */}
        <Route path="/" element={<MarketingHome />} />
        <Route path="/features" element={<MarketingFeatures />} />
        <Route path="/pricing" element={<MarketingPricing />} />

        {/* Public event RSVP (birthday parties, open days, etc.) */}
        <Route path="/rsvp/:token" element={<RsvpPage />} />

        {/* Legal + support — required by Apple App Store + Privacy Act */}
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/security" element={<SecurityPage />} />

        {/* Per-operator sites */}
        <Route path="/:slug" element={<SitePageOrMarketing />} />
        <Route path="/:slug/blog/:postSlug" element={<BlogPost />} />
        <Route path="*" element={<MarketingHome />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
